import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { panel } from "theme/colors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "redux/hooks";
import {
  selectAccessToken,
  selectAuthenticated,
  selectDeviceId,
  selectRefreshToken,
} from "redux/authSlice";
import { checkIdToken } from "utils/service";
import { usePopover } from "hooks/use-popover";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import MarkAsRead from "assets/icons1/MarkAsRead.svg";
import {
  calculateRelativeTime,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { Notification as NotificationData } from "./NotificationTypes";
import CloseIcon from "@mui/icons-material/Close";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  deleteAllNotifications,
  deleteNotification,
  markAsRead,
  markAsReadAll,
} from "./NotificationService";
import { selectTheme } from "redux/preferencesSlice";
import { parseHTML } from "screens/Tickets/ViewTicket/Components/CommentEditor";
import { BellNavIcon } from "utils/TopNavIcons";
import { topNavIconButtonSx } from "layouts/DrawerLayout/components/TopNav.styles";
import { topNav } from "theme/colors";
import EmptyState from "global/components/EmptyState/EmptyState";

/**
 * Placeholder row. Mirrors a real notification's geometry — same padding and
 * the same title / description / timestamp stack — so the list doesn't jump
 * when the worker's first payload lands.
 */
const NotificationSkeleton = ({ striped }: { striped: boolean }) => (
  <Stack
    spacing={0.5}
    sx={{
      px: "20px",
      py: "14px",
      backgroundColor: striped ? panel.stripe : "common.white",
    }}
  >
    <Skeleton
      variant="text"
      animation="wave"
      width="60%"
      height={14}
      sx={{ bgcolor: panel.skeleton }}
    />
    <Skeleton
      variant="text"
      animation="wave"
      width="90%"
      height={13}
      sx={{ bgcolor: panel.skeleton }}
    />
    <Skeleton
      variant="text"
      animation="wave"
      width="30%"
      height={12}
      sx={{ bgcolor: panel.skeleton }}
    />
  </Stack>
);

const Notification = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const preferredTheme = useAppSelector(selectTheme);
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  // Nothing else tracks "have we heard from the worker yet" — without it an
  // empty list on first paint is indistinguishable from a genuinely empty
  // inbox, and the panel would flash "No notification yet!" before the first
  // payload arrives.
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [intervalId, setIntervalId] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [hasMoreNotifs, setHasMoreNotifs] = useState<boolean>(false);
  const accessToken = useAppSelector(selectAccessToken);
  const refreshToken = useAppSelector(selectRefreshToken);
  const deviceId = useAppSelector(selectDeviceId);
  const observer = useRef<IntersectionObserver | null>(null);
  const notifs = useCallback(
    (node) => {
      if (observer.current) {
        (observer.current as IntersectionObserver).disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          if (totalCount >= 5 * page) {
            setPage((page) => page + 1);
            setHasMoreNotifs(true);
          } else {
            setHasMoreNotifs(false);
          }
        }
      });
      if (node && observer.current) {
        const intersectionObserver = observer.current as IntersectionObserver;
        intersectionObserver.observe(node);
      }
    },
    [notifications.length],
  );

  const notificationPopover = usePopover<any>();

  const notificationWorker: Worker = useMemo(
    () =>
      new Worker(
        new URL("workers/Notification/notifications.worker", import.meta.url),
      ),
    [],
  );

  useEffect(() => {
    return () => {
      notificationWorker.terminate();
    };
  }, []);

  const lastPollingKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      stopNotificationPolling();
      lastPollingKeyRef.current = null;
      return;
    }
    const pollingKey = `${accessToken}:${page}`;
    if (lastPollingKeyRef.current === pollingKey) return;
    lastPollingKeyRef.current = pollingKey;
    initNotificationPolling();
  }, [accessToken, page, isLoggedIn]);

  const initNotificationPolling = () => {
    notificationWorker.postMessage({
      type: "INIT",
      data: {
        page,
        accessToken,
        deviceId,
        intervalId,
      },
    });
  };

  const stopNotificationPolling = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (notificationWorker) {
      notificationWorker.terminate();
    }
  };

  const handleAllNotifs = async (type: "READ" | "DELETE") => {
    try {
      if (type === "READ") {
        await markAsReadAll();
      }
      if (type === "DELETE") {
        await deleteAllNotifications();
      }
      initNotificationPolling();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleNotification = async (type: "READ" | "DELETE", id: string) => {
    try {
      if (type === "READ") {
        await markAsRead(id);
      }
      if (type === "DELETE") {
        await deleteNotification(id);
      }
      initNotificationPolling();
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  // Data received from the worker thread
  notificationWorker.onmessage = async (payload: any) => {
    const { type, data } = payload.data;
    switch (type) {
      case "INTERVAL_ID": {
        setIntervalId(data.intervalId);
        break;
      }
      case "NOTIFICATION": {
        setUnreadCount(data.unreadCount);
        setTotalCount(data.totalCount);
        setNotifications(data.notifications);
        setHasLoaded(true);
        break;
      }
      case "TOKEN_EXPIRED": {
        try {
          await checkIdToken(accessToken, refreshToken);
        } catch (error: any) {
          if (intervalId) {
            clearInterval(intervalId);
            setIntervalId(null);
          }
          // Polling is dead — release the placeholders rather than leave the
          // panel shimmering forever.
          setHasLoaded(true);
        }
        break;
      }
      case "INTERNAL_SERVER_ERROR": {
        stopNotificationPolling();
        setHasLoaded(true);
        break;
      }
      default: {
        break;
      }
    }
  };

  // Both row controls share one box so the tick and the cross line up: an
  // 18px glyph centred in a 28px square, regardless of img vs svg.
  const rowActionSx = {
    width: 28,
    height: 28,
    p: 0,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const getNotificationsView = () => {
    // Cold load only. Once rows are on screen the poll refreshes them in
    // place, so re-showing placeholders would flash the panel on every tick.
    if (!hasLoaded && !isTruthy(notifications)) {
      return (
        <>
          {[0, 1, 2].map((index) => (
            <NotificationSkeleton key={index} striped={index % 2 === 0} />
          ))}
        </>
      );
    }

    if (!isTruthy(notifications)) {
      return (
        /* A popover widget — the compact size: no icon, no buttons. */
        <EmptyState size="compact" title="No notifications yet" />
      );
    }
    return notifications.map((notification, index: number) => {
      const isUnread = !notification.read;
      return (
        <Stack
          key={notification.notificationId ?? index}
          spacing={0.5}
          sx={{
            px: "20px",
            py: "14px",
            position: "relative",
            backgroundColor: isUnread ? panel.stripe : "common.white",
            "&:hover": {
              backgroundColor: panel.hover,
              // Row controls stay out of the way until the row is pointed at,
              // so the resting list reads as cleanly as the reference.
              "& .notification-row-actions": { opacity: 1 },
              "& .notification-row-dot": { opacity: 0 },
            },
          }}
          id={notifications.length === index + 1 ? "ref" : undefined}
          ref={notifications.length === index + 1 ? notifs : undefined}
        >
          <Typography
            sx={{
              pr: "34px",
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: 1.35,
              color: panel.title,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {notification.title}
          </Typography>
          <Typography
            sx={{
              pr: "34px",
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: 1.45,
              color: panel.bodyText,
              // `parseHTML` stamps an inline `padding: 2` on every element it
              // emits — including the synthetic <body> wrapper it starts from —
              // which nests up and pushes this line out of alignment with the
              // title and timestamp. Inline styles only yield to `!important`.
              "& *": { padding: "0 !important" },
            }}
          >
            {parseHTML(notification.description)}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", fontWeight: 500, color: panel.metaText }}
          >
            {calculateRelativeTime(notification.createdAt)}
          </Typography>

          {isUnread && (
            <Box
              className="notification-row-dot"
              sx={{
                position: "absolute",
                top: "20px",
                right: "20px",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: panel.unreadDot,
                transition: "opacity 0.15s",
              }}
            />
          )}
          <Stack
            className="notification-row-actions"
            direction="row"
            alignItems="center"
            spacing="2px"
            sx={{
              position: "absolute",
              // Centred on the dot this replaces: dot centre sits at 24px, so
              // a 28px control box starts at 10px.
              top: "10px",
              right: "12px",
              opacity: 0,
              transition: "opacity 0.15s",
            }}
          >
            {isUnread && (
              <Tooltip title="Mark as read" placement="top" arrow>
                <IconButton
                  onClick={() =>
                    handleNotification("READ", notification.notificationId)
                  }
                  aria-label="Mark as read"
                  sx={rowActionSx}
                >
                  <Box
                    component="img"
                    src={MarkAsRead}
                    alt=""
                    aria-hidden="true"
                    // Inline images sit on the text baseline, which pushed this
                    // out of line with the cross next to it.
                    sx={{ display: "block", width: 18, height: 18 }}
                  />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Remove" placement="top" arrow>
              <IconButton
                onClick={() =>
                  handleNotification("DELETE", notification.notificationId)
                }
                aria-label="Remove notification"
                sx={{ ...rowActionSx, color: panel.metaText }}
              >
                <CloseIcon sx={{ display: "block", fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      );
    });
  };

  const getNotificationSection = () => {
    return (
      <Stack>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: "20px",
            py: "16px",
            borderBottom: `1px solid ${panel.border}`,
          }}
        >
          <Typography
            component="h2"
            sx={{ fontSize: "17px", fontWeight: 700, color: panel.title }}
          >
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Box
              sx={{
                backgroundColor: panel.countPillBg,
                color: panel.countPillText,
                padding: "4px 10px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {unreadCount} new
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {isTruthy(notifications) && unreadCount > 0 && (
            <Button
              variant="text"
              onClick={() => handleAllNotifs("READ")}
              sx={{
                minWidth: 0,
                p: 0,
                fontSize: "13.5px",
                fontWeight: 700,
                textTransform: "none",
                color: "primary.main",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              {isDesktop ? (
                "Mark all read"
              ) : (
                <img
                  src={MarkAsRead}
                  alt="mark all read"
                  style={{ width: "20px", height: "20px" }}
                />
              )}
            </Button>
          )}
          <IconButton
            aria-label="Close notifications"
            onClick={() => notificationPopover.handleClose()}
            sx={{
              color: panel.metaText,
              p: "4px",
              "&:hover": {
                backgroundColor: theme.palette.signer.surface.muted,
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>

        <Stack
          sx={{ maxHeight: "350px", overflowY: "auto" }}
          aria-busy={!hasLoaded}
        >
          {getNotificationsView()}
          {hasMoreNotifs && notifications.length > 0 && (
            <Stack justifyContent="center" alignItems="center" my={2}>
              <CircularProgress size="1.5rem" />
            </Stack>
          )}
        </Stack>

        {/* Footer */}
        {isTruthy(notifications) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              borderTop: `1px solid ${panel.border}`,
              backgroundColor: panel.stripe,
            }}
          >
            <Button
              variant="text"
              onClick={() => handleAllNotifs("DELETE")}
              sx={{
                width: "100%",
                py: "12px",
                borderRadius: 0,
                fontSize: "14px",
                fontWeight: 600,
                textTransform: "none",
                color: panel.bodyText,
                "&:hover": {
                  backgroundColor: theme.palette.signer.surface.muted,
                },
              }}
            >
              Clear all notifications
            </Button>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Tooltip title="Notifications" placement="bottom" arrow>
        <Box
          component="button"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={notificationPopover.open}
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
          sx={topNavIconButtonSx}
          onClick={notificationPopover.handleOpen}
          ref={notificationPopover.anchorRef}
        >
          <Badge
            variant="dot"
            invisible={!(notifications.length && unreadCount > 0)}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: topNav.badge,
                width: 8,
                height: 8,
                minWidth: 8,
                borderRadius: "50%",
                top: "3px",
                right: "3px",
                border: `2px solid ${theme.palette.common.white}`,
                boxSizing: "content-box",
              },
            }}
          >
            <BellNavIcon sx={{ fontSize: 20 }} />
          </Badge>
        </Box>
      </Tooltip>

      <CustomPopper
        open={notificationPopover.open}
        anchorRef={notificationPopover.anchorRef.current}
        handleClose={notificationPopover.handleClose}
        children={getNotificationSection()}
        ariaLabel="Notifications"
        width={isDesktop ? "25rem" : "25rem"}
        // Rows and footer run edge to edge, so the panel carries no padding.
        padding={0}
        shadow={panel.shadow}
      />
    </>
  );
};

export default Notification;
